<template>
  <div class="modern-sponsors-showcase" v-if="sponsors.length > 0">
    <!-- Section Header -->
    <div class="showcase-header">
      <div class="header-icon-wrapper">
        <v-icon color="white" size="20">mdi-handshake</v-icon>
      </div>
      <h2 class="showcase-title">Our Sponsors</h2>
    </div>

    <!-- Marquee Container -->
    <div class="marquee-container" ref="marqueeContainer">
      <div class="marquee-content" ref="marqueeContent">
        <!-- Display all sponsors multiple times for seamless loop -->
        <div
          v-for="(sponsor, index) in [...sponsors, ...sponsors, ...sponsors]"
          :key="`sponsor-${index}`"
          class="sponsor-item"
        >
          <div class="modern-sponsor-card">
            <div class="sponsor-card-inner">
              <v-img
                :src="sponsor.url"
                class="sponsor-image"
                contain
              ></v-img>
              <div class="sponsor-overlay"></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
  <div v-else class="loading-message">
    Loading sponsors...
  </div>
</template>

<script>
import axios from 'axios'

export default {
  data() {
    return {
      sponsors: []
    }
  },
  async mounted() {
    await this.fetchSponsors()
  },
  methods: {
    async fetchSponsors() {
      try {
        const response = await axios.get('https://api.charlottetamilsangam.org/api/getSponsors')
        // Handle different possible response structures
        if (Array.isArray(response.data)) {
          this.sponsors = response.data.map(sponsor => ({
            url: sponsor.photo || sponsor.url || sponsor.imageUrl || sponsor.image
          }))
        } else if (response.data.data && Array.isArray(response.data.data)) {
          this.sponsors = response.data.data.map(sponsor => ({
            url: sponsor.photo || sponsor.url || sponsor.imageUrl || sponsor.image
          }))
        }
      } catch (error) {
        // Fallback to empty array if API fails
        this.sponsors = []
      }
    }
  }
}
</script>

<style scoped lang="scss">
* {
  font-family: "Avenir", Helvetica, Arial, sans-serif;
}

/* Modern Sponsors Showcase */
.modern-sponsors-showcase {
  background: linear-gradient(135deg, #4a5a8f 0%, #3d5a8c 50%, #2e6ba8 100%) !important;
  padding: 24px 0 16px 0 !important;
  position: relative !important;
  overflow: hidden !important;
}

.modern-sponsors-showcase::before {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  height: 4px !important;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #8ebf42 100%) !important;
  background-size: 200% 100% !important;
  animation: gradientFlow 3s ease infinite !important;
}

/* Showcase Header */
.showcase-header {
  display: flex !important;
  align-items: center !important;
  justify-content: flex-start !important;
  gap: 12px !important;
  margin-bottom: 16px !important;
  flex-wrap: wrap !important;
  padding: 0 24px !important;
}

.header-icon-wrapper {
  width: 32px !important;
  height: 32px !important;
  border-radius: 8px !important;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%) !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  box-shadow:
    0 4px 12px rgba(102, 126, 234, 0.4),
    inset 0 2px 4px rgba(255, 255, 255, 0.2) !important;
  position: relative !important;
  overflow: hidden !important;
}

.header-icon-wrapper::before {
  content: '' !important;
  position: absolute !important;
  top: -50% !important;
  left: -50% !important;
  width: 200% !important;
  height: 200% !important;
  background: linear-gradient(45deg, transparent, rgba(255, 255, 255, 0.3), transparent) !important;
  transform: rotate(45deg) !important;
  animation: shimmer 3s infinite !important;
}

.showcase-title {
  font-size: 22px !important;
  font-weight: 700 !important;
  color: white !important;
  margin: 0 !important;
  letter-spacing: -0.3px !important;
  text-shadow: 0 3px 10px rgba(0, 0, 0, 0.3) !important;
}

/* Marquee Container */
.marquee-container {
  width: 100% !important;
  overflow: hidden !important;
  padding: 8px 0 !important;
  position: relative !important;
}

.marquee-content {
  display: flex !important;
  gap: 32px !important;
  animation: marqueeScroll 40s linear infinite !important;
  will-change: transform !important;
  overflow-x: auto !important;
  scroll-behavior: smooth !important;
  scrollbar-width: none !important;
  -ms-overflow-style: none !important;
}

.marquee-content::-webkit-scrollbar {
  display: none !important;
}

.marquee-content:hover {
  animation-play-state: paused !important;
}

.sponsor-item {
  flex-shrink: 0 !important;
}

/* Modern Sponsor Card */
.modern-sponsor-card {
  width: 240px !important;
  height: 140px !important;
  border-radius: 20px !important;
  background: linear-gradient(145deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%) !important;
  backdrop-filter: blur(20px) !important;
  border: 2px solid rgba(255, 255, 255, 0.15) !important;
  box-shadow:
    0 8px 32px rgba(0, 0, 0, 0.3),
    inset 0 2px 4px rgba(255, 255, 255, 0.1) !important;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  position: relative !important;
  overflow: hidden !important;
}

.modern-sponsor-card::before {
  content: '' !important;
  position: absolute !important;
  top: 0 !important;
  left: 0 !important;
  right: 0 !important;
  height: 3px !important;
  background: linear-gradient(90deg, #667eea 0%, #764ba2 50%, #8ebf42 100%) !important;
  background-size: 200% 100% !important;
  animation: gradientFlow 3s ease infinite !important;
}

.modern-sponsor-card:hover {
  transform: translateY(-8px) scale(1.05) !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  box-shadow:
    0 16px 48px rgba(0, 0, 0, 0.4),
    0 0 40px rgba(102, 126, 234, 0.3),
    inset 0 2px 4px rgba(255, 255, 255, 0.2) !important;
}

.sponsor-card-inner {
  position: relative !important;
  width: 100% !important;
  height: 100% !important;
  padding: 12px !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
  overflow: hidden !important;
}

.sponsor-image {
  width: 100% !important;
  height: 100% !important;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1) !important;
  filter: brightness(1.1) contrast(1.1) !important;
}

.modern-sponsor-card:hover .sponsor-image {
  transform: scale(1.1) !important;
  filter: brightness(1.2) contrast(1.2) !important;
}

.sponsor-overlay {
  position: absolute !important;
  inset: 0 !important;
  background: linear-gradient(135deg,
    rgba(102, 126, 234, 0) 0%,
    rgba(118, 75, 162, 0.1) 100%) !important;
  opacity: 0 !important;
  transition: opacity 0.4s ease !important;
}

.modern-sponsor-card:hover .sponsor-overlay {
  opacity: 1 !important;
}

/* Animations */
@keyframes gradientFlow {
  0%, 100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

@keyframes shimmer {
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(100%) translateY(100%) rotate(45deg);
  }
}

@keyframes marqueeScroll {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

/* Responsive Design */
@media (max-width: 960px) {
  .modern-sponsors-showcase {
    padding: 20px 0 12px 0 !important;
  }

  .showcase-header {
    margin-bottom: 14px !important;
  }

  .showcase-title {
    font-size: 20px !important;
  }

  .header-icon-wrapper {
    width: 30px !important;
    height: 30px !important;
  }

  .modern-sponsor-card {
    width: 220px !important;
    height: 130px !important;
  }

  .sponsor-card-inner {
    padding: 12px !important;
  }

  .marquee-content {
    gap: 24px !important;
  }
}

@media (max-width: 600px) {
  .modern-sponsors-showcase {
    padding: 16px 0 8px 0 !important;
  }

  .showcase-header {
    margin-bottom: 12px !important;
    gap: 10px !important;
    padding: 0 16px !important;
  }

  .header-icon-wrapper {
    width: 28px !important;
    height: 28px !important;
  }

  .header-icon-wrapper .v-icon {
    font-size: 18px !important;
  }

  .showcase-title {
    font-size: 18px !important;
  }

  .marquee-container {
    padding: 8px 0 !important;
  }

  .modern-sponsor-card {
    width: 160px !important;
    height: 100px !important;
    border-radius: 16px !important;
  }

  .modern-sponsor-card::before {
    height: 2px !important;
  }

  .sponsor-card-inner {
    padding: 10px !important;
  }

  .marquee-content {
    gap: 16px !important;
    animation: marqueeScroll 20s linear infinite !important;
  }
}
</style>

